import { Box, Group, Stack, TextInput, Title } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IChecklistPopulated } from "@mctypes/checklist.types";
import { ISpecificationTable } from "@mctypes/frontend.types";
import { IMinUserDepricated, IPNSProps, IProjectDeprecated, IValueLabel } from "@mctypes/global.types";
import { IChecklistStatus, IChecklistTemplateTypesEnums, ICheckTypes } from "@schemas/checklistSchema";
import { ColDef, RowSelectionOptions, SideBarDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { Avatar, CreateNewButton, FailedAlert, MCLoader, McTitle } from "../../../components";
import McDropdownNew from "../../../components/McDropdown/MCDropdownNew";
import HierarchyTreeSelector from "../../../components/HierarchyTreeSelector/HierarchyTreeSelector";
import TemplateSelector, {
  ITemplate,
  TemplateSelectorType,
} from "../../../components/TemplateSelector/TemplateSelector";
import { selectAgGridStateByKey } from "../../../global/agGridStateSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useAgGridReduxState } from "../../../hooks/useAgGridReduxState";
import { formatImportedExcelData } from "../../../mc-shared/checklistexcelvalidation/checklistExcelValidationUtils";
import { CHECKLIST_ZOD_EXCEL_VALIDATION } from "../../../mc-shared/checklistexcelvalidation/checklistExcelValidatorSchemas";
import { MCApplicationsUrlEnum } from "../../../mc-shared/sharedConstants";
import CheckTypesSelector from "./CheckTypesSelector";
import { IDocumentManagers } from "./ChecklistAdmin";
import { getChecklistColumns } from "./ChecklistColums";
import { TypeOfChecklistEnum } from "./ChecklistConstants";
import "./ChecklistTable.css";
import { doAddChecklist } from "./checklistAPI";
import {
  useCreateChecklistFromImportedExcelMutation,
  useGetAllChecklistTemplatesQuery,
  useGetUniqueChecklistTagsQuery,
} from "./checklistRTK";
import { setSelectedPnsFilterItemInRedux } from "./checklistSlice";
import { CHECKLIST_STATUS_DROPDOWN_VALUES, ChecklistTemplateStatusEnum } from "./constants";

interface ChecklistBoardSelectorProps {
  name: string;
  templateType: IChecklistTemplateTypesEnums;
  checklists: IChecklistPopulated[];
  loading: boolean;
  error: boolean;
  createNewTooltip: string;
}

const ChecklistBoardSelector: React.FC<ChecklistBoardSelectorProps> = ({
  templateType,
  checklists,
  error,
  loading,
  name,
  createNewTooltip,
}) => {
  const gridState = useSelector(selectAgGridStateByKey(name));

  let history = useHistory();
  const isTemplate = templateType !== "";
  const project: IProjectDeprecated = useAppSelector((state) => state.adminReducer.project);
  const { selectedPnsFilterItem } = useAppSelector((state) => state.checklistReducer);
  const [modalIsOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [pnsItems, setPnsItems] = useState<{ comboLabel: string; _id: string; parentId: string; props: IPNSProps }[]>(
    [],
  );
  const [selectedPns, setSelectedPns] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate>(null);
  const [convertedTemplates, setConvertedTemplates] = useState<ITemplate[]>(null);
  const [selectedStatus, setSelectedStatus] = useState<IValueLabel>(null);
  const [checkTypes, setCheckTypes] = useState<ICheckTypes>(null);

  const dispatch = useAppDispatch();

  const [
    createChecklistFromExcelMutation,
    { isLoading: createChecklistFromExcelLoading, isError: createChecklistFromExcelError },
  ] = useCreateChecklistFromImportedExcelMutation();

  useEffect(() => {
    setPnsItems(
      project?.pns?.map((pns) => ({
        ...pns,
        comboLabel: `${pns.props.pnsId} - ${pns.props.title}`,
      })),
    );
  }, [project?.pns]);

  const { data: tags } = useGetUniqueChecklistTagsQuery({ projectId: project._id });

  const { data: templates } = useGetAllChecklistTemplatesQuery({ projectId: project._id });

  const baseColumns = useMemo<ColDef[]>(() => {
    return getChecklistColumns(isTemplate, pnsItems, tags?.map((t) => t._id) || []);
  }, [tags, isTemplate, pnsItems]);

  const { onGridReady, onStateUpdated, setQuickFilterText, mergedColumns } = useAgGridReduxState(name, baseColumns);

  const goToChecklist = (checklistId: string) => {
    const routeType = templateType === "" ? TypeOfChecklistEnum.CHECKLIST : TypeOfChecklistEnum.TEMPLATE;
    history.push(`/project/${project._id}/checklist/${routeType}/${checklistId}`);
  };

  const createNew = async (templateId: string) => {
    const checklist = await doAddChecklist(
      selectedTitle,
      selectedPns,
      project._id,
      templateId,
      selectedStatus.value as IChecklistStatus,
      checkTypes,
      isTemplate,
      templateType,
    );

    goToChecklist(checklist._id);
  };

  const toggle = () => {
    setSelectedTitle("");
    setModalOpen(!modalIsOpen);
  };

  useEffect(() => {
    const activeTemplates = templates?.filter((cl) => cl.status === "ACTIVE") || [];

    const newEmptyTemplate: IChecklistPopulated = {
      _id: "0",
      aboutText: "",
      templateDescription: "En helt tom mal om du vil sette opp en mal fra bunnen av.",
      checkTypes: null,
      createdAt: null,
      createdBy: null,
      templateType: "MC",
      isTemplate: true,
      project: null,
      name: "Tom mal",
      tags: [],
      status: ChecklistTemplateStatusEnum.IN_DEVELOPMENT,
      generatedFromTemplate: null,
      lastUpdatedBy: null,
      assignedTo: null,
      pns: "",
      stampSettings: {
        yes: { color: "#00975f", icon: "check", text: "JA" },
        no: { color: "#dc3545", icon: "close", text: "NEI" },
        ir: { color: "#f8992e", icon: "ban", text: "IR" },
        na: { color: "#0000004F", icon: "circle", text: "-" },
        uav: { color: "#4085b6ff", icon: "exclamation-triangle", text: "UAV" },
      },
      templateCategory: null,
      includeIrOnDefaultRoles: false,
      includeUavOnDefaultRoles: false,
      documentCategoryCode: "SKL",
      documentCategoryCode2: "",
      documentManagers: null,
      disciplineCode: "",
      subDiscipline: "",
      competenceNetwork: "",
    };

    activeTemplates?.push(newEmptyTemplate);

    const convertedChecklistTemplates: ITemplate[] = activeTemplates?.map((template) => {
      return {
        _id: template._id,
        name: template.name,
        templateType: template.templateType as TemplateSelectorType,
        description: template.templateDescription,
        admin: template.admin,
        category: template.disciplineCode ?? "",
        documentManagers: template.documentManagers,
      };
    });

    setConvertedTemplates(convertedChecklistTemplates);
    onTemplateSelected(convertedChecklistTemplates?.find((t) => t.name === "Tom mal"));
  }, [checklists, templates]);

  const STATUSES = [CHECKLIST_STATUS_DROPDOWN_VALUES.active, CHECKLIST_STATUS_DROPDOWN_VALUES.in_development];

  const pnsDropdownOptions = useMemo<IValueLabel[]>(
    () => [
      { value: "", label: "Ingen" },
      ...pnsItems.map((item) => ({ value: item.props.pnsId, label: item.comboLabel })),
    ],
    [pnsItems],
  );

  const selectedPnsOption = useMemo(
    () => (selectedPns !== "" ? pnsDropdownOptions.find((item) => item.value === selectedPns) ?? null : null),
    [pnsDropdownOptions, selectedPns],
  );

  const resetCheck3Name = (types: ICheckTypes): ICheckTypes => ({
    ...types,
    check3: {
      ...types.check3,
      name: "",
    },
  });

  const onTemplateSelected = (template: ITemplate) => {
    setSelectedTemplate(template);

    if (template == null) {
      return;
    }

    if (template.name === "Tom mal") {
      setCheckTypes({
        check1: {
          active: true,
          name: "EK",
        },
        check2: {
          active: false,
          name: "SK",
        },
        check3: {
          active: false,
          name: "",
        },
      });
      return;
    }
    const selectedTemplate = checklists.find((t) => t._id === template._id);
    if (selectedTemplate != null) {
      setCheckTypes(resetCheck3Name(selectedTemplate.checkTypes));
    }
  };

  const selectedPnsIds = useMemo(() => {
    const id = pnsItems.find((item) => item.props.pnsId === selectedPnsFilterItem)?._id;
    return id != null ? [id] : [];
  }, [selectedPnsFilterItem, pnsItems]);

  const PNS_COLLAPSE_LOCAL_STORAGE_KEY = `${project._id}_checklist_pnsCollapse`;

  const pnsIsOpen: boolean = useMemo(() => {
    const valInLocalStorage = localStorage.getItem(PNS_COLLAPSE_LOCAL_STORAGE_KEY);
    if (valInLocalStorage === null) {
      return true;
    }
    return valInLocalStorage === "true";
  }, [PNS_COLLAPSE_LOCAL_STORAGE_KEY]);

  const createChecklistFromExcel = async (data: string[][]) => {
    try {
      const checklistRows = formatImportedExcelData(data);
      const resp = await createChecklistFromExcelMutation({
        params: {
          projectId: project._id,
        },
        body: {
          checklistImportRows: checklistRows as any,
          status: selectedStatus.value as IChecklistStatus,
          name: selectedTitle,
          pns: selectedPns,
          checkTypes,
          templateType,
        },
      });
      if ("data" in resp && resp.data != null) {
        goToChecklist(resp.data._id);
      }
    } catch (error) {
      alert("Feil ved konvertering av excel");
      console.error("Error creating checklist from excel", error);
    }
  };

  const isError = createChecklistFromExcelError === true || error === true;
  const isLoading = createChecklistFromExcelLoading === true || loading === true;

  const sideBar = useMemo(() => {
    const sb: SideBarDef = {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
      ],
      position: "left",
    };
    return sb;
  }, []);

  const defaultColDef = useMemo(
    () => ({
      suppressHeaderMenuButton: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    }),
    [],
  );

  const rowData = useMemo(() => checklists.filter((c) => c.templateType === templateType), [checklists, templateType]);

  const onCellClicked = useCallback(
    (e) => {
      if (e.colDef.field === "id") return; // ignore clicks on the link cell
      e.api.deselectAll();
      goToChecklist(e.data._id);
    },
    [goToChecklist],
  );

  const rowSelection: RowSelectionOptions<IChecklistPopulated> = useMemo(
    () => ({ mode: "singleRow", checkboxes: false }),
    [],
  );

  return (
    <>
      {isError && <FailedAlert />}
      {isLoading === true && <MCLoader loading />}
      {isError === false && isLoading === false && (
        <>
          <div className="d-flex flex-column w-100 h-100">
            <>
              <Group align="center" gap="xs" py="xs" px={"xxs"}>
                <CreateNewButton size={"sm"} onClick={() => setModalOpen(true)} tooltip={createNewTooltip} />
                <DebouncedSearchInput onChange={setQuickFilterText} value={gridState.quickFilter} />
              </Group>
              <div
                className="d-flex w-100"
                style={{
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {pnsItems?.length > 0 && isTemplate === false && (
                  <HierarchyTreeSelector
                    style={{
                      minWidth: "200px",
                      maxWidth: "300px",
                      background: "#ededed",
                    }}
                    nodeList={pnsItems}
                    idProp={"pnsId"}
                    titleProp={"title"}
                    onSelectionChange={(node) => dispatch(setSelectedPnsFilterItemInRedux(node[0]?.props?.pnsId))}
                    selectedNodeIds={selectedPnsIds}
                    isCollapsed={!pnsIsOpen}
                    localStorageCollapseKey={PNS_COLLAPSE_LOCAL_STORAGE_KEY}
                  />
                )}

                <Box className="checklistTable ag-theme-quartz" h="calc(100vh - 200px)" w="100%" ml="xs">
                  <AgGridReact
                    onGridReady={onGridReady}
                    onColumnMoved={(e) => {
                      if (e.finished) onStateUpdated();
                    }}
                    onColumnVisible={onStateUpdated}
                    onColumnPinned={onStateUpdated}
                    onFilterChanged={onStateUpdated}
                    onSortChanged={onStateUpdated}
                    onColumnResized={(e) => {
                      if (e.finished) onStateUpdated();
                    }}
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={100}
                    paginationPageSizeSelector={[100, 200, 500, 1000]}
                    rowHeight={48}
                    columnDefs={mergedColumns}
                    suppressClickEdit={true}
                    rowSelection={rowSelection}
                    animateRows={false}
                    suppressContextMenu={true}
                    onCellClicked={onCellClicked}
                    localeText={AG_GRID_LOCALE_NOR}
                    sideBar={sideBar}
                  />
                </Box>
              </div>
            </>
          </div>
          {modalIsOpen === true && (
            <TemplateSelector
              bottomDescriptionSection={
                <TemplateResponsibleSection documentManagers={selectedTemplate?.documentManagers} />
              }
              onTemplateSelected={onTemplateSelected}
              templates={convertedTemplates}
              header={"Ny sjekkliste"}
              app={{
                name: MCApplicationsUrlEnum.CHECKLIST,
                icon: "appIcons/reportGenerator.svg",
                iconColor: "#6791a1",
              }}
              onCreate={(template) => {
                createNew(template._id);
              }}
              closeModal={toggle}
              isTitleEmpty={selectedTitle === ""}
              isTemplateProject={false}
              descriptionAndAdmin={true}
              showInstanceButton={false}
              showProjectButton={isTemplate === false}
              preSelectedTemplate={convertedTemplates.find((template) => template.name === "Tom mal")}
              disableOpprettButton={selectedStatus == null || selectedTitle === ""}
              excelImport={{
                enabled: true,
                zodValidation: CHECKLIST_ZOD_EXCEL_VALIDATION,
                onCreateFromExcel: createChecklistFromExcel,
                specificationTable: CHECKLIST_EXCEL_SPECIFICATION_TABLE,
              }}
            >
              <Stack gap="sm" px="xl" py={0} pt="sm">
                <Title order={5} m="xs">
                  Ny sjekkliste
                </Title>
                <McTitle title="Navn*">
                  <TextInput
                    required
                    autoFocus
                    onChange={(t) => setSelectedTitle(t.currentTarget.value)}
                    value={selectedTitle}
                    placeholder={"Navn*"}
                  />
                </McTitle>
                <McTitle title="Status*">
                  <McDropdownNew
                    type="text"
                    options={STATUSES}
                    onChange={(_oldStatus, status) => setSelectedStatus(status)}
                    currentValue={selectedStatus}
                    defaultTitle="Velg status"
                    displayKey="label"
                    justify="space-between"
                  />
                </McTitle>
                <McTitle title="PNS">
                  <McDropdownNew
                    type="text"
                    options={pnsDropdownOptions}
                    onChange={(_oldPns, pns) => setSelectedPns(pns?.value ?? "")}
                    currentValue={selectedPnsOption}
                    defaultTitle="Velg PNS"
                    displayKey="label"
                    withFilter={pnsDropdownOptions.length > 10}
                    justify="space-between"
                  />
                </McTitle>
                {checkTypes != null && (
                  <McTitle title="Roller">
                    <CheckTypesSelector inputIsEditable={false} checkTypes={checkTypes} onChange={setCheckTypes} />
                  </McTitle>
                )}
              </Stack>
            </TemplateSelector>
          )}
        </>
      )}
    </>
  );
};

export default ChecklistBoardSelector;

export const CustomSearchInput = (props) => {
  let input;

  const handleClick = () => {
    props.onSearch(input.value);
  };

  return (
    <input
      placeholder="Søk..."
      className="mcinput mr-2"
      style={{ maxWidth: "15rem" }}
      ref={(n) => (input = n)}
      type="text"
      onChange={handleClick}
    />
  );
};

export const CHECKLIST_EXCEL_SPECIFICATION_TABLE: ISpecificationTable = {
  description:
    "Må starte med en kategoritittel med heltalls-ID: 1, 2, 3 osv. og deretter underkategorier med desimaler: 1.1, 1.2 osv.",
  columns: [
    {
      field: "nr",
      headerName: "Nr",
    },
    {
      field: "description",
      headerName: "Spørsmål",
    },
  ],
  data: [
    {
      nr: "1",
      description: "RIGG OG DRIFT",
    },
    {
      nr: "1.1",
      description: "Rigging av byggeplass?",
    },
    {
      nr: "1.2",
      description: "....",
    },
    {
      nr: "2",
      description: "FORBEREDELSE - TERRENGARBEIDER",
    },
    {
      nr: "2.1",
      description: "....",
    },
  ],
};

const TemplateResponsibleSection = ({ documentManagers }: { documentManagers: IDocumentManagers }) => {
  if (
    documentManagers == null ||
    (documentManagers?.preparedBy == null &&
      documentManagers?.approvedBy == null &&
      documentManagers?.checkedBy == null)
  ) {
    return null;
  }

  return (
    <div className="d-flex flex-column">
      {documentManagers?.preparedBy && <DocManagerAvatar user={documentManagers.preparedBy} title="Utarbeidet av" />}
      {documentManagers?.approvedBy && <DocManagerAvatar user={documentManagers.approvedBy} title="Godkjent av" />}
      {documentManagers?.checkedBy && <DocManagerAvatar user={documentManagers.checkedBy} title="Kontrollert av" />}
    </div>
  );
};

const DocManagerAvatar = ({ user, title }: { user: IMinUserDepricated; title: string }) => {
  return (
    <div className="d-flex align-items-center mt-1">
      <Avatar id={user?._id} name={user?.name} size="sm" />
      <div className="d-flex flex-column">
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontWeight: "bold",
            fontSize: "12px",
            color: "#6c757d",
          }}
        >
          {user.name}
        </div>
      </div>
    </div>
  );
};

export const DebouncedSearchInput: React.FC<{ onChange: (value: string) => void; value: string }> = React.memo(
  ({ onChange, value: v }) => {
    const [value, setValue] = useState<string>(v ?? "");
    const [debouncedValue] = useDebouncedValue(value, 500);

    useEffect(() => {
      if (onChange) {
        onChange(debouncedValue);
      }
    }, [debouncedValue, onChange]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    }, []);

    return (
      <TextInput
        size="sm"
        style={{ maxWidth: "15rem" }}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder="Søk..."
      />
    );
  },
);
